/**
 * Terms, Privacy and the DPA, as content rather than markup.
 *
 * These were the three links the footer used to carry pointing at `/`, which is how they came to
 * be removed: a dead "DPA" link on a product that argues for regulatory defensibility is worse
 * than no link. Removing them left the other problem, though — Wami sells to healthcare and
 * finance teams, and a B2B buyer in either cannot sign without these existing. So here they are.
 *
 * Two rules were followed while writing them, and both matter more than the prose:
 *
 * **Only claim what the code actually does.** Every factual claim here was checked against the
 * implementation, and the first draft got four of them wrong: it promised deletion within 30
 * days of closure when no closure flow or purge job exists, said support access is recorded in
 * the audit log when there is no support-access mechanism to record, said Wami runs in Europe
 * while the managed-database default provisioned in Virginia, and named Neon as *the* database
 * host when it is one of three options. There is no claim of SOC 2, ISO 27001, HIPAA
 * certification or any audit that has not happened, because a buyer's security review asks for
 * the report and not having one is a worse moment than never having claimed it.
 *
 * **Lead with the WhatsApp transport risk.** Wami connects through the WhatsApp Web protocol,
 * not the official Business API. That is already stated plainly on `/trust`, and burying it in
 * clause 14 of the terms while the marketing site is candid about it would be the one genuinely
 * dishonest thing this document could do.
 *
 * `LEGAL_REVIEW_REQUIRED` is rendered at the top of all three pages. These are drafted from what
 * the system does; they are not a substitute for a lawyer reviewing them against the
 * jurisdictions Wami actually sells into, and the placeholders below have to be filled before
 * any of this is binding on anyone.
 */

/** Fill these in before publishing. They are deliberately obvious rather than plausible. */
export const LEGAL_ENTITY = {
  name: '[Legal entity name]',
  address: '[Registered address]',
  jurisdiction: '[Governing jurisdiction]',
  contact: '[Contact email]',
  dpoContact: '[Data protection contact]',
} as const

export const LEGAL_LAST_UPDATED = '[Effective date]'

export const LEGAL_REVIEW_REQUIRED =
  'Draft. This document is written from what the product actually does, but it has not been ' +
  'reviewed by counsel and the entity details are not yet filled in. Do not rely on it as a ' +
  'binding agreement until it has been.'

export interface LegalSection {
  heading: string
  paragraphs: string[]
  /** Rendered as a list under the paragraphs when present. */
  bullets?: string[]
}

export interface LegalDocument {
  slug: 'terms' | 'privacy' | 'dpa'
  title: string
  eyebrow: string
  lead: string
  sections: LegalSection[]
}

/**
 * Everything Wami sends customer data to in order to run the service. Customer-initiated
 * integrations (Notion, Confluence, Slack, a CRM) are deliberately not on this list: they are
 * connections the customer chooses and authorises, and the data moves because they asked for it.
 */
/**
 * Everything Wami sends customer data to in order to run the service.
 *
 * Derived from the outbound calls this repository actually makes, not from a template's idea of
 * a typical SaaS stack, and re-checked against the code rather than against the last version of
 * this list. Two corrections worth remembering, because both were wrong on the first pass:
 * the database row named only Neon when a workspace's database can equally be Supabase or a
 * Postgres server the operator runs, and Meta was missing entirely even though every single
 * message transits WhatsApp — the Terms covered that as a *risk* while the Privacy Policy said
 * nothing about it as a *data flow*.
 *
 * Customer-initiated integrations (Notion, Confluence, HubSpot, Zoho, Google Sheets, a webhook)
 * are deliberately not here: those are connections the customer chooses and authorises, and the
 * data moves because they asked for it.
 */
export const SUBPROCESSORS: { name: string; purpose: string; data: string }[] = [
  {
    name: 'Contabo (hosting)',
    purpose: 'Runs the application servers and stores uploaded and received message media.',
    data: 'All service data, at rest and in transit through the application.',
  },
  {
    name: 'Meta Platforms (WhatsApp)',
    purpose:
      'Carries every message. Wami connects through the WhatsApp Web protocol, so the transport ' +
      'is WhatsApp itself — see the Terms for what that means for your number.',
    data: 'Message content, phone numbers, and group membership, as with any WhatsApp client.',
  },
  {
    name: 'Your workspace database host',
    purpose:
      'Holds the conversations, contacts and tickets for your workspace. Which company this is ' +
      'depends on what you chose: a Postgres server the operator runs, Neon, or Supabase.',
    data: 'Contacts, conversations, messages, tickets, and workspace configuration.',
  },
  {
    name: 'Amazon Web Services',
    purpose:
      'Named for completeness: every region Neon offers is an AWS region, so a workspace on Neon ' +
      'is on AWS underneath. Not applicable if your database is self-hosted.',
    data: 'Whatever the workspace database holds.',
  },
  {
    name: 'Stripe',
    purpose: 'Takes card payments and manages subscriptions outside India.',
    data: 'Billing contact and payment details. Stripe handles card numbers; Wami never sees them.',
  },
  {
    name: 'Razorpay',
    purpose: 'Takes payments and manages subscriptions for Indian customers.',
    data: 'Billing contact and payment details. Razorpay handles card numbers; Wami never sees them.',
  },
  {
    name: 'Resend, or your own SMTP server',
    purpose: 'Sends sign-in codes, invitations and export links.',
    data: 'Email addresses and the content of those messages.',
  },
  {
    name: 'Your configured model provider',
    purpose:
      'Powers AI replies and summaries — only if you turn them on and supply your own API key. ' +
      'There is no shared Wami model account, so without a key these features stay off.',
    data:
      'The message text and knowledge-base content needed to answer, sent to your own provider ' +
      'account under your own agreement with them.',
  },
]

export const TERMS: LegalDocument = {
  slug: 'terms',
  title: 'Terms of Service',
  eyebrow: 'Legal',
  lead:
    'The agreement between you and Wami. The first section is the one that actually matters, so ' +
    'it is first rather than buried.',
  sections: [
    {
      heading: 'How Wami connects to WhatsApp, and what that risks',
      paragraphs: [
        'Wami links your number the same way WhatsApp Web does: you scan a QR code from the ' +
          'phone that owns the number. Wami is not the official WhatsApp Business API and is not ' +
          'an approved Business Solution Provider. Meta does not endorse, certify or support it.',
        'WhatsApp’s terms of service prohibit accessing the service through unofficial ' +
          'clients. Using Wami may therefore breach your agreement with WhatsApp, and WhatsApp ' +
          'may restrict, suspend or permanently ban the number you connect. That decision is ' +
          'Meta’s alone. We cannot appeal it for you, we cannot predict it, and we are not ' +
          'liable for it.',
        'You accept that risk when you connect a number. If it is not a risk your organisation ' +
          'can carry, the official Business API through an approved provider is the correct ' +
          'choice, and we would rather tell you that here than take your money first.',
      ],
    },
    {
      heading: 'Your account',
      paragraphs: [
        'You must be able to enter into a contract, and you are responsible for what happens ' +
          'under your account, including anything done by people you invite to your workspace.',
        'Keep your sign-in credentials to yourself. Tell us promptly if you believe someone ' +
          'else has access.',
      ],
    },
    {
      heading: 'Acceptable use',
      paragraphs: ['You agree not to use Wami to:'],
      bullets: [
        'send unsolicited bulk messages to people who have not agreed to hear from you',
        'break any law that applies to you or to the people you message',
        'impersonate anyone, or misrepresent who is sending a message',
        'work around the rate limits, seat counts or usage limits of your plan',
        'attempt to access another customer’s workspace or data',
      ],
    },
    {
      heading: 'Payment',
      paragraphs: [
        'Paid plans are billed in advance for the period you choose. Prices exclude taxes ' +
          'unless we say otherwise. Payments are handled by Stripe or Razorpay depending on ' +
          'where you are billed; we do not store card numbers.',
        'Cancelling stops the next renewal. Access continues until the end of the period you ' +
          'have already paid for. We do not refund part-used periods unless the law where you ' +
          'are requires it.',
      ],
    },
    {
      heading: 'Your data',
      paragraphs: [
        'Your conversations, contacts and files remain yours. We process them to run the ' +
          'service for you and for nothing else — in particular, we do not sell them, and we do ' +
          'not use them to train models. The Privacy Policy explains what we hold and why, and ' +
          'the Data Processing Addendum governs our handling of personal data on your behalf.',
        'You can export your data at any time while your account is active.',
      ],
    },
    {
      heading: 'Availability',
      paragraphs: [
        'We work to keep Wami running, but we do not promise uninterrupted service and there is ' +
          'no uptime guarantee on any plan today. Message delivery ultimately depends on ' +
          'WhatsApp, which is outside our control.',
        'We may change or discontinue features. Where a change materially reduces what you are ' +
          'paying for, we will tell you before it takes effect.',
      ],
    },
    {
      heading: 'Ending the agreement',
      paragraphs: [
        'You can close your account at any time. We may suspend or close an account that ' +
          'breaches these terms, does not pay, or puts the service or other customers at risk.',
        'After closure we delete workspace data on the schedule set out in the Privacy Policy. ' +
          'Export anything you need first.',
      ],
    },
    {
      heading: 'Liability',
      paragraphs: [
        'To the extent the law allows, Wami is provided as it is, without warranties. We are ' +
          'not liable for indirect or consequential loss, lost profits, or lost business — ' +
          'including any loss arising from WhatsApp restricting or banning a connected number.',
        'Where liability cannot be excluded, it is capped at the amount you paid us in the ' +
          'twelve months before the claim.',
      ],
    },
    {
      heading: 'Changes and governing law',
      paragraphs: [
        'We may update these terms. For material changes we will give notice in the product or ' +
          'by email before they take effect; continuing to use Wami afterwards means you accept ' +
          'them.',
        `These terms are governed by the laws of ${LEGAL_ENTITY.jurisdiction}.`,
      ],
    },
  ],
}

export const PRIVACY: LegalDocument = {
  slug: 'privacy',
  title: 'Privacy Policy',
  eyebrow: 'Legal',
  lead:
    'What Wami holds, why it holds it, and who else touches it. Written to be read, not to be ' +
    'survived.',
  sections: [
    {
      heading: 'Two different roles',
      paragraphs: [
        'For your account — who signed up, who is on the team, what you pay — Wami is the ' +
          'controller, and this policy governs it.',
        'For the contents of your workspace — the people you message, what they say, the files ' +
          'they send — you are the controller and Wami is your processor. We handle that data on ' +
          'your instructions, under the Data Processing Addendum.',
      ],
    },
    {
      heading: 'What we collect',
      paragraphs: ['Account and billing data, which you give us:'],
      bullets: [
        'your name, work email and company name',
        'your workspace settings, industry and locale',
        'billing contact details and payment records (card numbers stay with Stripe or Razorpay)',
        'sign-in events, including the IP address a sign-in code was requested from, kept to ' +
          'rate-limit abuse',
      ],
    },
    {
      heading: 'What we process for you',
      paragraphs: [
        'When you connect a WhatsApp number, the messages, contacts, media and group membership ' +
          'that flow through it are stored in your workspace’s own database so the product ' +
          'can work: so a chat has history, so a ticket has an SLA, so a search finds something.',
        'Each workspace gets its own database for that content, rather than a shared table with ' +
          'a tenant column. Account-level records — who is on the team, your plan, your ' +
          'sign-in sessions, your SSO settings — live in one shared control database keyed by ' +
          'workspace, which is the distinction a security reviewer will want drawn.',
        'If you turn on number masking, phone numbers are hidden from the roles you scope, ' +
          'inside the product as well as in exports.',
      ],
    },
    {
      heading: 'What we do not do',
      paragraphs: [
        'We do not sell your data. We do not share it for advertising. We do not use your ' +
          'messages, contacts or documents to train any model, our own or anyone else’s.',
        'We do not read your conversations as a matter of course. Wami has no support ' +
          'impersonation feature — there is no button anyone here can press to enter your ' +
          'workspace. If you ask us to investigate something, an engineer with production ' +
          'credentials reaches the database directly, under confidentiality obligations. Be ' +
          'aware that this route is outside your audit log, which records actions taken through ' +
          'the product rather than at the database.',
      ],
    },
    {
      heading: 'Who else touches it',
      paragraphs: [
        'Running the service means using other companies. Each one below gets only what it ' +
          'needs for its stated job. Integrations you connect yourself — Notion, Confluence, a ' +
          'CRM — are not on this list, because that data moves because you told it to.',
      ],
    },
    {
      heading: 'AI features',
      paragraphs: [
        'AI replies, summaries and knowledge search are off unless you turn them on. When they ' +
          'are on, the message text needed to answer is sent to the model provider configured ' +
          'for your workspace.',
        'A workspace using its own API key sends that text to its own provider account under its ' +
          'own agreement, and it does not pass through a shared Wami model account. There is no ' +
          'shared Wami model account: without a key of your own, these features stay off.',
        'One exception worth naming: the embeddings that make knowledge search work are computed ' +
          'by an endpoint the operator configures for the whole deployment, not one you choose ' +
          'per workspace. On this deployment it is self-hosted.',
      ],
    },
    {
      heading: 'How long we keep it',
      paragraphs: [
        'Workspace content is kept for as long as your account exists. There is no automatic ' +
          'deletion on inactivity and no self-service account closure yet — ask us to close an ' +
          'account and we delete its workspace data within 30 days of that request.',
        'Audit log retention is yours to configure, including keeping it forever.',
        'Sign-in codes stop working ten minutes after they are issued, and the rows are cleared ' +
          'on an ongoing cycle after that rather than at the moment of expiry. Billing records ' +
          'are kept as long as tax law requires.',
      ],
    },
    {
      heading: 'Where it lives',
      paragraphs: [
        'The application servers run in Europe. Your workspace database lives wherever you chose ' +
          'when you set it up — the default is Frankfurt, and London, Virginia, Oregon and ' +
          'Singapore are all offered. If you brought your own database, it is wherever you put it.',
        'Where data is processed outside the UK or EEA, the transfer is covered by Standard ' +
          'Contractual Clauses or an equivalent mechanism.',
      ],
    },
    {
      heading: 'Security',
      paragraphs: [
        'Traffic is encrypted in transit. Passwords are hashed, never stored in a readable form. ' +
          'Sign-in codes are stored as keyed hashes, so a copy of the database does not yield ' +
          'working codes. Integration credentials and SSO client secrets are encrypted at rest ' +
          'with AES-GCM. Access to production is limited to people who need it.',
        'We hold no third-party security certification today. If your procurement process needs ' +
          'one, tell us before you buy rather than after.',
      ],
    },
    {
      heading: 'Your rights',
      paragraphs: [
        'Depending on where you live you may have the right to see, correct, export, or delete ' +
          'the personal data we hold about you, to object to processing, or to complain to your ' +
          'data protection authority.',
        `For account data, contact ${LEGAL_ENTITY.dpoContact}. If your request concerns data in ` +
          'someone’s workspace, they are the controller and we will pass your request to them.',
      ],
    },
  ],
}

export const DPA: LegalDocument = {
  slug: 'dpa',
  title: 'Data Processing Addendum',
  eyebrow: 'Legal',
  lead:
    'How Wami handles personal data on your behalf. This forms part of the Terms of Service for ' +
    'every customer; you do not need to sign a separate copy.',
  sections: [
    {
      heading: 'Roles',
      paragraphs: [
        'You are the controller of the personal data in your workspace. Wami is your processor ' +
          'and acts only on your documented instructions — which, in the ordinary course, are ' +
          'the actions you take in the product.',
        'We will tell you if an instruction appears to us to breach applicable data protection ' +
          'law, and we will not follow it in the meantime.',
      ],
    },
    {
      heading: 'Scope of processing',
      paragraphs: [
        'Subject matter: providing the Wami service. Duration: for as long as your account is ' +
          'open, plus the deletion window below.',
        'Categories of data subject: your customers and contacts, and the people on your team. ' +
          'Categories of personal data: names, phone numbers, email addresses, message content, ' +
          'files, and any custom fields you choose to record. You control what you put in, ' +
          'including whether it includes special-category data.',
      ],
    },
    {
      heading: 'Confidentiality and security',
      paragraphs: [
        'Everyone we allow near production data is bound by confidentiality obligations. ' +
          'Technical and organisational measures are described in the Security section of the ' +
          'Privacy Policy and form part of this addendum.',
        'We will not materially reduce those measures during your subscription.',
      ],
    },
    {
      heading: 'Subprocessors',
      paragraphs: [
        'You give general authorisation for the subprocessors listed in the Privacy Policy. ' +
          'Each is bound by data protection terms no less protective than these.',
        'We will give at least 30 days’ notice before adding or replacing one. If you ' +
          'reasonably object on data protection grounds, you may terminate the affected part of ' +
          'the service and receive a refund of anything paid for after the termination date.',
      ],
    },
    {
      heading: 'Helping you meet your obligations',
      paragraphs: [
        'The product gives you direct access, export and deletion tools, which will usually be ' +
          'the fastest way to answer a data subject request. Where a request needs us, we will ' +
          'help, taking into account the nature of the processing.',
        'We will also give you the information you reasonably need for a data protection impact ' +
          'assessment or a consultation with a supervisory authority.',
      ],
    },
    {
      heading: 'Personal data breaches',
      paragraphs: [
        'If we become aware of a breach affecting your personal data, we will notify you without ' +
          'undue delay and in any event within 72 hours of becoming aware.',
        'That notice will describe what we know, what we are doing, and what we recommend you ' +
          'do — including where the facts are still incomplete. We will not wait for a full ' +
          'picture before telling you.',
      ],
    },
    {
      heading: 'International transfers',
      paragraphs: [
        'Where personal data leaves the UK or EEA, the transfer is covered by Standard ' +
          'Contractual Clauses or another lawful transfer mechanism, together with any ' +
          'supplementary measures the circumstances require.',
      ],
    },
    {
      heading: 'Audit',
      paragraphs: [
        'On reasonable notice, and no more than once a year unless a regulator or a breach ' +
          'requires otherwise, we will make available the information needed to demonstrate ' +
          'compliance with this addendum and allow an audit by you or an auditor you appoint, ' +
          'subject to confidentiality.',
      ],
    },
    {
      heading: 'Return and deletion',
      paragraphs: [
        'You may export your data at any time while your account is open. On closure, workspace ' +
          'personal data is deleted within 30 days, except where we are required by law to keep ' +
          'it — in which case we keep only what is required, for only as long as required.',
        'Backups are overwritten on their ordinary cycle and are not restored except to recover ' +
          'from a failure.',
      ],
    },
  ],
}

export const LEGAL_DOCUMENTS = [TERMS, PRIVACY, DPA] as const
